import {useQuery} from "@apollo/client";
import TypeUtils from "../../../utils/TypeUtils";
import QueryUtils from "./QueryUtils";
import graphqlErrorFormatter from "./graphqlErrorFormatter.js";
import {useCallback} from "react";

/**
 * Function similar to useLazyQuery from Apollo GraphQL, but its callback is a promise (it is not the case with the
 * callback returned by useLazyQuery). Beware that callback from this function takes variables as argument (because it is the
 * refetch function returned by useQuery), whereas callback from useLazyQuery takes options.
 * @param query Query
 * @param queryScope Query scope (first child of Query)
 * @param queryName Name of the query
 * @param options Options
 * @return {((function(*=): Promise<{readonly data?: *}>)|<any, >)[]}
 */
export default function useDeferredQuery(query, queryScope, queryName, options = {}) {

  // Force skip: true
  const augmentedOptions = TypeUtils.assign(options, { skip: true });

  const {refetch, ...result} = useQuery(query, augmentedOptions);

  // The callback is the refetch function, but we remove the query name from the fetched data
  const fetch = useCallback((variables) => {
    return refetch(variables)
      .then(({ data }) => Promise.resolve(QueryUtils.removeQueryScopeAndName(data, queryScope, queryName)))
      .catch(errors => {
        return Promise.reject(graphqlErrorFormatter(errors));
      });
  }, [refetch, queryScope, queryName]);

  return {fetch, ...result};
}
